import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import NotificationArea from 'component/NotificationArea';
import { Login } from '@code-yellow/spider';
import AppHeader from './AppHeader';
import { withRouter } from 'react-router-dom';
import Router from './Router';
import StartupError from './StartupError';
import RuntimeError from './RuntimeError';
import { AppContainer, Body } from 're-cy-cle';
import * as Sentry from '@sentry/react';
import { theme } from 'styles';
import { ModuleRepository } from '_iae/module/repository';
import { renderTabTitle } from '@code-yellow/spider';
import styled from 'styled-components';

const pathsThatSkipAuthentication = [
    /^\/login\/forgot$/,
    /^\/user\/\d+\/reset-password\/[^/]+$/,
    /^\/register/,
    /^\/demo/,
];

const StyledAppContainer = styled(AppContainer)`
    aside {
        /* Blur the content behind the aside */
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px); /* Safari support */
    }

    /* [T54191] Keep the app header pinned at the top of the viewport in case
       anything ever does overflow the shell (e.g. a screen without its own
       scroll areas). Sticky (not fixed) keeps its space in the flex column,
       so nothing shifts. z-index sits above screen content (e.g. the
       drop-rejection toast at 300) but below Semantic UI modals. */
    > header {
        position: sticky;
        top: 0;
        z-index: 900;
    }
`

/* [T54191] Fill exactly the space below the app header and clip overflow:
   full-viewport screens (milestone detail/status, capacity planner) manage
   their own internal scrolling. `min-height: 0` beats the flexbox automatic
   minimum size, which would otherwise let tall content grow the page. The
   same applies to the direct children (e.g. the spider ContentContainer
   that wraps every screen): without it their content-based minimum grows
   past the shell and gets clipped with no way to reach it. */
const ClippingBody = styled(Body)`
    min-height: 0;
    overflow: hidden;

    > * {
        min-height: 0;
    }
`


@withRouter
@observer
export default class App extends Component {
    static propTypes = {
        store: PropTypes.object.isRequired,
        moduleRepository: PropTypes.instanceOf(ModuleRepository).isRequired,
        location: PropTypes.object,
    };

    static childContextTypes = {
        viewStore: PropTypes.object,
    };

    getChildContext() {
        return {
            viewStore: this.props.store,
        };
    }

    componentDidCatch(err) {
        this.hasCrashed = true;
        if (process.env.CY_FRONTEND_SENTRY_DSN) {
            Sentry.captureException(err);
            Sentry.showReportDialog();
        }
    }

    @observable hasCrashed = false;

    render() {
        const { store, location, moduleRepository } = this.props;

        if (this.hasCrashed) {
            return <RuntimeError />;
        }

        const isUnauthenticatedRoute = pathsThatSkipAuthentication.some(regex =>
            regex.test(location.pathname)
        );

        let content = null;

        // By default do not show the app headers for the unauthenticated routes,
        // Since these are generally special pages, without navigatoin.
        let showAppHeader = !isUnauthenticatedRoute;
        if (
            store.isAuthenticated || isUnauthenticatedRoute
        ) {
            content = <Router store={store} moduleRepository={moduleRepository} />;
        } else if (store.bootstrapCode === 200) {
            showAppHeader = false;
            content = <Login viewStore={store} />;
        } else if (store.bootstrapCode !== null) {
            // The not null check is important, since we don't want to flash a startup error while the XHR request is running.
            return <StartupError code={store.bootstrapCode} />;
        }

        return (
            <React.Fragment>
                {renderTabTitle()}
                <StyledAppContainer data-theme={JSON.stringify(theme)}>
                    {showAppHeader && (
                        <AppHeader store={store} location={location} moduleRepository={moduleRepository} />
                    )}
                    <ClippingBody>{content}</ClippingBody>
                    {store.currentModal ? (
                        <store.currentModal.render
                            viewStore={store}
                            {...store.currentModal}
                        />
                    ) : null}
                    <NotificationArea store={store} />
                </StyledAppContainer>
            </React.Fragment>
        );
    }

}
